import { MERGE_FIELDS, type Card, type CardPatch, type Clock, type FieldPatch, type MergeField, type SettingPatch } from '@hmawiw/shared';
import { create } from 'zustand';
import { getDb } from '../db/handle';
import { readMeta, setMeta } from '../db/idb';
import { isNative } from '../native/platform';
import { LOCAL_BOARD, useCards, type OutboxAck } from '../store/cards';
import { useSession } from '../store/session';
import { useEvents } from '../store/events';
import { useSettings } from '../store/settings';
import { ApiError, NetworkError, api } from './api';
import { pauseLive, resumeLive, startLive, stopLive } from './live';

export type SyncPhase = 'idle' | 'syncing' | 'offline' | 'error' | 'signedOut';

export interface SyncState {
  phase: SyncPhase;
  pending: number;
  lastSyncAt: number | null;
  error: string | null;
  /** The wake-up stream from the server is open: other devices' edits arrive within a second. */
  live: boolean;
}

export const useSync = create<SyncState>(() => ({ phase: 'idle', pending: 0, lastSyncAt: null, error: null, live: false }));

/** Long enough to coalesce a burst of taps, short enough that the other devices see the edit within a second. */
const FLUSH_DELAY = 200;
const PULL_INTERVAL = 5 * 60_000;
const BACKOFF_MIN = 1000;
const BACKOFF_MAX = 5 * 60_000;

let started = false;
let inFlight = false;
let again = false;
let backoff = BACKOFF_MIN;
/** The server sequence this device has pulled through; a wake-up at or below it is an echo. */
let knownSeq = 0;
/** A wake-up that came in while a flush was running: the next flush is owed. */
let wantSeq = 0;

function wake(seq: number): void {
  if (seq <= knownSeq) return;
  if (inFlight) {
    wantSeq = Math.max(wantSeq, seq);
    return;
  }
  scheduleFlush(0);
}
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let pullTimer: ReturnType<typeof setInterval> | null = null;
const cleanups: (() => void)[] = [];

/** Hooks a device runtime can register, such as rescheduling local notifications after a pull. */
export const syncHooks: { afterPull: (() => Promise<void> | void)[] } = { afterPull: [] };

function setPhase(phase: SyncPhase, error: string | null = null): void {
  useSync.setState({ phase, error, pending: pendingCount(), ...(phase === 'idle' ? { lastSyncAt: Date.now() } : {}) });
}

function pendingCount(): number {
  const cards = useCards.getState();
  return Object.keys(cards.outbox).length + cards.dismissQueue.length + useSettings.getState().dirtyEntries().length + useEvents.getState().pendingEvents().length;
}

function online(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

export function startSync(): void {
  if (started) return;
  started = true;
  cleanups.push(useCards.subscribe((s, prev) => {
    if (s.outbox !== prev.outbox || s.dismissQueue !== prev.dismissQueue) {
      useSync.setState({ pending: pendingCount() });
      if (Object.keys(s.outbox).length > Object.keys(prev.outbox).length || s.dismissQueue.length > prev.dismissQueue.length) scheduleFlush();
      else if (s.outbox !== prev.outbox && Object.keys(s.outbox).length) scheduleFlush();
    }
  }));
  cleanups.push(useSettings.subscribe((s, prev) => {
    if (s.entries !== prev.entries && s.dirtyEntries().length) scheduleFlush();
  }));
  cleanups.push(useEvents.subscribe((s, prev) => {
    if (s.events !== prev.events && s.pendingEvents().length) scheduleFlush();
  }));
  if (typeof window !== 'undefined') {
    const onOnline = () => {
      scheduleFlush(0);
      resumeLive();
    };
    const onOffline = () => pauseLive();
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        scheduleFlush(0);
        resumeLive();
      } else pauseLive();
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    document.addEventListener('visibilitychange', onVisible);
    cleanups.push(() => window.removeEventListener('online', onOnline));
    cleanups.push(() => window.removeEventListener('offline', onOffline));
    cleanups.push(() => document.removeEventListener('visibilitychange', onVisible));
  }
  pullTimer = setInterval(() => scheduleFlush(0), PULL_INTERVAL);
  void readMeta<number>(getDb(), 'lastSeq').then((v) => {
    knownSeq = Math.max(knownSeq, v ?? 0);
  });
  startLive({
    token: () => useSession.getState().token,
    wake,
    onState: (live) => useSync.setState({ live }),
    onUnauthorized: () => void useSession.getState().handleUnauthorized(),
  });
  scheduleFlush(0);
}

export function stopSync(): void {
  if (!started) return;
  started = false;
  stopLive();
  for (const c of cleanups.splice(0)) c();
  if (flushTimer) clearTimeout(flushTimer);
  if (pullTimer) clearInterval(pullTimer);
  flushTimer = null;
  pullTimer = null;
  knownSeq = 0;
  wantSeq = 0;
  setPhase('signedOut');
}

export function scheduleFlush(delay = FLUSH_DELAY): void {
  if (!started) return;
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush();
  }, delay);
}

/** A sync the user asked for: runs now, or waits for the run in progress, and resolves when that run is over. */
export function syncNow(): Promise<void> {
  if (!started) return Promise.resolve();
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (!inFlight) return flush();
  again = true;
  return new Promise((resolve) => {
    const unsub = useSync.subscribe((s) => {
      if (s.phase !== 'syncing') {
        unsub();
        resolve();
      }
    });
  });
}

function fieldClocks(fields: Record<string, FieldPatch>): Partial<Record<MergeField, Clock>> {
  const out: Partial<Record<MergeField, Clock>> = {};
  for (const f of MERGE_FIELDS) {
    const fp = fields[f];
    if (fp) out[f] = fp.clock;
  }
  return out;
}

/** Push everything waiting, then pull. Single-flight; a call during a run queues one more. */
export async function flush(): Promise<void> {
  const session = useSession.getState();
  if (session.status !== 'authed' || !session.token) {
    setPhase('signedOut');
    return;
  }
  if (session.user?.mustChangePassword) return;
  if (!online()) {
    setPhase('offline');
    return;
  }
  if (inFlight) {
    again = true;
    return;
  }
  inFlight = true;
  setPhase('syncing');
  const token = session.token;
  try {
    const cardsState = useCards.getState();
    const entries = Object.values(cardsState.outbox).slice(0, 500);
    const dirty = useSettings.getState().dirtyEntries();
    const events = useEvents.getState().pendingEvents().slice(0, 1000);
    if (entries.length || dirty.length || events.length) {
      const patches: CardPatch[] = entries.map((e) => ({
        id: e.id,
        boardId: e.boardId === LOCAL_BOARD ? (session.boardId ?? e.boardId) : e.boardId,
        createdAt: e.createdAt,
        fields: e.fields,
      }));
      const settings: Record<string, SettingPatch> = {};
      for (const d of dirty) settings[d.key] = { json: d.json, clock: d.clock, base: d.base };
      const res = await api.push(token, { cards: patches, settings, events });
      const acks: OutboxAck[] = [];
      const drops: string[] = [];
      const remote: Card[] = [];
      for (const r of res.results) {
        const entry = entries.find((e) => e.id === r.id);
        if (!entry) continue;
        if (r.status === 'rejected') {
          console.error('sync: the server rejected a card', r);
          drops.push(r.id);
          continue;
        }
        acks.push({ id: r.id, sent: fieldClocks(entry.fields as Record<string, FieldPatch>) });
        if (r.card) remote.push(r.card);
      }
      await useCards.getState().ackOutbox(acks, drops);
      await useCards.getState().applyRemoteCards(remote);
      for (const [key, s] of Object.entries(res.settings ?? {})) {
        const sent = dirty.find((d) => d.key === key);
        if (!sent) continue;
        if (s.status === 'stale') await useSettings.getState().applyRemote(key, s.row.json, s.row.clock, true);
        else await useSettings.getState().markSynced(key, sent.clock);
      }
      if (events.length) await useEvents.getState().markSent((res.events ?? []).map((e) => e.id));
      const storedInstall = await readMeta<string>(getDb(), 'installId');
      if (storedInstall && storedInstall !== res.installId) {
        await reseed(res.installId);
        return;
      }
      if (!storedInstall) await setMeta(getDb(), 'installId', res.installId);
    }
    for (const id of [...useCards.getState().dismissQueue]) {
      try {
        await api.dismissConflict(token, id);
      } catch (e) {
        if (!(e instanceof ApiError && e.status === 404)) throw e;
      }
      await useCards.getState().clearDismissals([id]);
    }
    await pull(token);
    backoff = BACKOFF_MIN;
    setPhase('idle');
  } catch (e) {
    await handleFailure(e);
  } finally {
    inFlight = false;
    const owed = again || wantSeq > knownSeq;
    again = false;
    wantSeq = 0;
    if (owed) scheduleFlush(200);
  }
}

async function handleFailure(e: unknown): Promise<void> {
  if (e instanceof ApiError && e.status === 401) {
    setPhase('signedOut');
    await useSession.getState().handleUnauthorized();
    return;
  }
  if (e instanceof ApiError && e.status === 403 && e.code === 'PASSWORD_CHANGE_REQUIRED') {
    useSession.getState().markPasswordRequired();
    setPhase('idle');
    return;
  }
  if (e instanceof ApiError && e.status === 400) {
    // A patch the server will never accept must not block the rest of the outbox.
    const details = Array.isArray(e.details) ? (e.details as { id?: string }[]) : [];
    const ids = [...new Set(details.map((d) => d.id).filter((id): id is string => typeof id === 'string'))];
    console.error('sync: invalid patches dropped', e.details);
    if (ids.length) await useCards.getState().ackOutbox([], ids);
    setPhase('error', e.message);
    scheduleFlush(BACKOFF_MIN);
    return;
  }
  const offline = e instanceof NetworkError || !online();
  setPhase(offline ? 'offline' : 'error', e instanceof Error ? e.message : String(e));
  backoff = Math.min(backoff * 2, BACKOFF_MAX);
  scheduleFlush(backoff + Math.random() * 1000);
}

export async function pull(token: string): Promise<void> {
  const db = getDb();
  for (;;) {
    const since = (await readMeta<number>(db, 'lastSeq')) ?? 0;
    let res;
    try {
      res = await api.pull(token, since);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        const reason = (e.details as { reason?: string } | undefined)?.reason;
        if (reason === 'purged') await resyncAfterPurge(token);
        else await reseed(null);
        return;
      }
      throw e;
    }
    const storedInstall = await readMeta<string>(db, 'installId');
    if (storedInstall && storedInstall !== res.installId) {
      await reseed(res.installId);
      return;
    }
    if (!storedInstall) await setMeta(db, 'installId', res.installId);
    const cards = useCards.getState();
    await cards.applyRemoteCards(res.cards);
    await cards.applyRemoteConflicts(res.conflicts);
    for (const s of res.settings) await useSettings.getState().applyRemote(s.key, s.json, s.clock, false);
    await useEvents.getState().applyRemote(res.events ?? []);
    await setMeta(db, 'lastSeq', res.seq);
    knownSeq = res.seq;
    if (!res.hasMore) break;
  }
  if (isNative) for (const hook of syncHooks.afterPull) await hook();
}

/** The server was wiped or restored: our copy is the truth, push all of it again. */
export async function reseed(installId: string | null): Promise<void> {
  const db = getDb();
  await setMeta(db, 'lastSeq', 0);
  knownSeq = 0;
  if (installId) await setMeta(db, 'installId', installId);
  else {
    const tx = db.transaction('meta', 'readwrite');
    void tx.store.delete('installId');
    await tx.done;
  }
  await useCards.getState().requeueAll();
  await useSettings.getState().requeueAll();
  await useEvents.getState().requeueAll();
  again = true;
}

/** Old tombstones are gone on the server: pull everything and drop what it no longer has. */
async function resyncAfterPurge(token: string): Promise<void> {
  const db = getDb();
  await setMeta(db, 'lastSeq', 0);
  const seen = new Set<string>();
  for (;;) {
    const since = (await readMeta<number>(db, 'lastSeq')) ?? 0;
    const res = await api.pull(token, since);
    for (const c of res.cards) seen.add(c.id);
    const cards = useCards.getState();
    await cards.applyRemoteCards(res.cards);
    await cards.applyRemoteConflicts(res.conflicts);
    for (const s of res.settings) await useSettings.getState().applyRemote(s.key, s.json, s.clock, false);
    await useEvents.getState().applyRemote(res.events ?? []);
    await setMeta(db, 'lastSeq', res.seq);
    knownSeq = res.seq;
    if (!res.hasMore) break;
  }
  const before = Object.keys(useCards.getState().cards);
  await useCards.getState().pruneMissing(seen);
  const after = new Set(Object.keys(useCards.getState().cards));
  await useEvents.getState().dropForCards(before.filter((id) => !after.has(id)));
}
